import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { Box, IconButton, Button, ArrowBackIcon, CloseIcon, LaunchOutlinedIcon, Typography } from 'src/components';
import { useIsMobile } from 'src/hooks';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

export default function UserStaticMapHeader({ coords, onClose, onFlyTo }) {
    const mapRef = useRef(null);
    const containerRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!coords?.length || !containerRef.current) return;

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/streets-v12', 
            center: coords,
            zoom: 4.5,
            interactive: false,
            dragPan: false,
            scrollZoom: false,
            doubleClickZoom: false,
            boxZoom: false,
            dragRotate: false,
            keyboard: false,
            attributionControl: false
        });

        mapRef.current = map;

        new mapboxgl.Marker({ color: '#0077ff', scale: 0.7 })
            .setLngLat(coords)
            .addTo(map);

        return () => {
            map.remove();
        };
    }, [coords]);

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%', zIndex: 0 }}>
            {/* Map container */}
            <Box
                ref={containerRef}
                sx={{
                    width: '100%',
                    height: '100%',
                    '& .mapboxgl-canvas': {
                        borderRadius: 0,
                    },
                }}
            />

            {/* Location Button */}
            <Box sx={{
                position: 'absolute',
                bottom: 50,
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                pointerEvents: 'none'
            }}>
                <Button
                    variant="text"
                    size="small"
                    onClick={onFlyTo}
                    startIcon={<LaunchOutlinedIcon sx={{width: 12, height: 12}} />}
                    sx={{
                        px: 1,
                        bgcolor: 'rgba(240, 240, 240, 0.7)',
                        borderRadius: 2,
                        cursor: 'pointer',
                        pointerEvents: 'auto'
                    }}
                    >
                    <Typography variant="body3" fontWeight={600}>Location</Typography>
                </Button>
            </Box>

            {/* Back or Close Icon */}
            <IconButton
                onClick={onClose}
                size="small"
                sx={{
                    position: 'absolute',
                    top: isMobile ? 'calc(12px + var(--safe-area-top))' : 12,
                    left: isMobile ? 12 : 'auto',
                    right: isMobile ? 'auto' : 12,
                    zIndex: 1,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                }}
                >
                {isMobile ? <ArrowBackIcon /> : <CloseIcon />}
            </IconButton>
        </Box>
    );
}
